import Head from 'next/head';
import { RECENT_ARTICLES_LIMIT } from '../../config';
import paginationQuery from '../../common/helpers/paginationQuery';
import CmsClient from '../../common/clients/CmsClient';
import parseEntity from '../../common/helpers/parseEntity';
import { FunctionComponent } from 'react';
import StaticPage from '../../components/StaticPage';
import { BlogPost, StaticPageProps } from '../../components/StaticPage/types';
import getConfigWithRequest from '../../common/helpers/getConfigWithRequest';
import { cache, generateKey } from '../../common/StaticCache';
import { getFooterMenu, prepareFooter } from '../../common/helpers/getFooterMenu';
import { PageProps, RESOURCE } from '../../common/types/CmsEntities';
import ApplyLaptopMobileBottom from './components/ApplyLaptopMobileBottom';
import { Theme, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { KnownBreakpoints, THEME_BREAKPOINTS } from '../../common/constants';

export const getServerSideProps = async ({
    req,
    params,
}: any): Promise<{ props: PageProps }> => {
    const { slug } = params;
    const { cmsApiUrl } = await getConfigWithRequest(req);
    const cacheKey = generateKey('blog-post', slug);
    const cachedData = await cache.get(cacheKey);

    if (cachedData) {
        return cachedData;
    }

    const [
        { data: menu },
        { data: blogList },
        { data: page },
        { data: posts },
        { data: sidebar },
        { data: footer },
        { data: footerMenu },
    ] = await Promise.all([
        CmsClient.getEntities(req, RESOURCE.menu),
        CmsClient.getEntities(req, RESOURCE.blogList),
        CmsClient.getEntityBySlug(req, RESOURCE.blogPost, slug),
        CmsClient.getEntities(req,
            RESOURCE.blogPost,
            paginationQuery(0, RECENT_ARTICLES_LIMIT),
        ),
        CmsClient.getEntities(req, RESOURCE.sidebar),
        CmsClient.getEntities(req, RESOURCE.footer),
        getFooterMenu({ cmsApiUrl }),
    ]);

    const parsedPage = parseEntity(page);
    const parsedMenu = parseEntity(menu);
    const parsedFooter = parseEntity(footer);
    const fullPage = {
        id: parsedPage.id,
        header: parseEntity(blogList).header,
        ...parsedPage,
    };

    const result = {
        props: {
            menu: parsedMenu,
            page: fullPage,
            posts: posts || [],
            sidebar: parseEntity(sidebar),
            footer: prepareFooter({
                footer: parsedFooter,
                menu: footerMenu,
            }),
        },
    };

    await cache.set(cacheKey, result);

    return result;
};

type BlogPostPageProps = StaticPageProps & {
    posts: BlogPost[];
};

const BlogPostPage: FunctionComponent<BlogPostPageProps> = ({
    menu,
    page,
    posts,
    sidebar,
    footer,
}) => {
    const theme = useTheme<Theme>();
    const isDesktop = useMediaQuery(
        theme.breakpoints.up(THEME_BREAKPOINTS[KnownBreakpoints.laptop]),
    );

    const sidebarTitle = {
        id: 99999,
        sectionType: 'sidebarTitle',
        title: { name: 'Recent Articles', color: theme.palette.text.primary },
    };
    const recentPosts = posts.map(p => ({
        id: p.id,
        sectionType: 'recentPostCard',
        slug: p.slug,
        title: p.title,
        text: p.previewText,
    }));
    const blogPost = page?.content.find(el => el.sectionType === 'blogPost');
    const sideBar = {
        ...sidebar.content[0],
        disableTransformation: false,
        blogPost,
    };
    const fullSidebar = {
        ...sidebar,
        content: isDesktop
            ? [
                sideBar,
                sidebarTitle,
                ...recentPosts,
            ]
            : [
                sidebarTitle,
                ...recentPosts,
                sideBar,
            ],
    };
    const staticPageProps = {
        menu,
        page,
        sidebar: fullSidebar,
        footer,
    };

    const { title, description, keywords } = page;

    return <>
    {/*
        title, meta or any other elements (e.g. script) need to be contained as
        direct children of the Head element, or wrapped into maximum one level
        of <React.Fragment> or arrays—otherwise the tags won't be correctly
        picked up on client-side navigations.
        https://nextjs.org/docs/pages/api-reference/components/head#use-minimal-nesting
    */}
    <Head>
        <title>{ title }</title>
        { description && <meta name="description" content={ description } /> }
        { keywords && <meta name="keywords" content={ keywords } /> }
    </Head>
    {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
    {/* @ts-ignore */}
    <StaticPage
        { ...staticPageProps }
        mobileLaptopBottomSticky={ <ApplyLaptopMobileBottom/> }
    />
</>; };

export default BlogPostPage;
